import React, { Dispatch, SyntheticEvent, useState } from 'react';
import { IDesignerProfile } from '@components/pages/DesignerProfile/graphql/designer-profile-query.graphql';
import { IDesignerBoard } from '@components/pages/DesignerProfile/graphql/designer-boards.graphql';
import { useTheme } from '@components/theme';
import classNames from 'classnames';
import SET_DESIGNER_OPINION from '@graphql/Designer/mutations/setDesignerOpinion.graphql';
import { useMutation } from '@apollo/client';
import LikeButton from '@components/shared/buttons/LikeButton/LikeButton';
import useElementScrollPosition from '@hooks/useScrollPosition/useScrollPosition';
import Loader from '@components/shared/Loader/Loader';
import Profile from '../Designer/Profile/Profile';
import Boards from '../Boards/Boards';
import DesignerVideo from '../Designer/Video/Video';
import About from '../Designer/About/About';
import FAQ from '../Designer/FAQ/FAQ';
import Tabs, { TABS } from '../shared/Tabs/Tabs';
import ChooseDesignerCTA from '../Designer/CTA/ChooseDesignerCTA';

import styles from './DesktopVersion.module.scss';
import Testimonials from '../Testimonials/Testimonials';
import { useBookDesignerAndProceedToPurchase } from '../../DesignerProfileAdmin/PreferButton/utils';

interface IDesktopVersionProps {
  className?: string;
  designer: IDesignerProfile;
  initialBoards: IDesignerBoard[];
  tab: TABS;
  setTab: Dispatch<any>;
}

const DesktopVersion = ({
  className, designer, initialBoards, tab, setTab
}: IDesktopVersionProps) => {
  const theme = useTheme();
  const tabs: TABS[] = ['portfolio', 'profile'];
  const [isFavorite, setFavorite] = useState(false);
  const [updateDesignerOpinion] = useMutation(SET_DESIGNER_OPINION);
  const scrollPosition = useElementScrollPosition(typeof window !== 'undefined' ? window : null, 10);
  const isScrolled = scrollPosition && scrollPosition.y > 1;

  const {
    bookDesignerAndProceedToPurchase,
    isChangingDesigner
  } = useBookDesignerAndProceedToPurchase();

  const chooseDesigner = async () => {
    bookDesignerAndProceedToPurchase(designer);
  };

  if (designer.testimonials?.length) tabs.push('reviews');

  const handleFavorite = async (ev: SyntheticEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    setFavorite(!isFavorite);

    try {
      await updateDesignerOpinion({
        variables: {
          designer: designer.id,
          opinion: Number(!isFavorite)
        }
      });
    } catch (error) {
      setFavorite(!isFavorite);
    }
  };

  return (
    <>
      {
        isChangingDesigner && (
          <div className={styles.LoadingLogo}><Loader /></div>
        )
      }
      <div
        className={classNames(
          theme.styles.Body,
          styles.Root,
          theme.styles.Container,
          className,
        )}
        data-test="desktop-version"
      >
        <div className={classNames(
          styles.SideInfo,
          { [styles.scrolled]: isScrolled }
        )}
        >
          <div className={styles.ProfileCard}>
            <div className={styles.LikeWrapper}>
              <LikeButton onClick={handleFavorite} title={'Favorite this designer'} liked={isFavorite} />
            </div>

            <Profile designer={designer} />
            <ChooseDesignerCTA text={`Work with ${designer.firstName}`} chooseDesigner={chooseDesigner} />
          </div>
        </div>
        <div className={styles.Main}>
          <Tabs tabs={tabs} set={setTab} selectedTab={tab} />
          { renderContent(tab, initialBoards, designer)}
        </div>
      </div>
    </>
  );
};

function renderContent(
  tab: TABS,
  boards: IDesignerBoard[],
  designer: IDesignerProfile
): React.ReactNode {
  switch (tab) {
    case 'portfolio':
      return <Boards initialBoards={boards} designer={designer} />;
    case 'profile':
      return (
        <div className={ styles.Profile }>
          <div className={styles.Half}>
            <About designer={designer} />
            <div className={styles.VideoWrapper}>
              <DesignerVideo className={styles.Video} designer={designer} />
            </div>
          </div>
          <div className={styles.Half}>
            <FAQ designer={designer} />
          </div>
        </div>
      );
    case 'reviews':
      return <Testimonials testimonials={designer.testimonials} />;
    default:
      return <></>;
  }
}

export default DesktopVersion;
